import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  Clusterer,
  GeolocationControl,
  Map,
  Placemark,
  TrafficControl,
  TypeSelector,
  withYMaps,
} from '@pbe/react-yandex-maps'
import type ymaps from 'yandex-maps'
import type { City, Salon, SalonPageVars } from 'api/api.types'
import { getSalons } from 'src/store/data.store'
import { formatPhone } from 'utils/formatPhone'
import { Loader } from 'components/Utility/Loader/Loader'
import placemark1 from 'assets/placemarks/placemark1.svg'
import placemark2 from 'assets/placemarks/placemark2.svg'
import placemark3 from 'assets/placemarks/placemark3.svg'
import placemark4 from 'assets/placemarks/placemark4.svg'
import placemark12 from 'assets/placemarks/placemark12.svg'
import placemark13 from 'assets/placemarks/placemark13.svg'
import placemark14 from 'assets/placemarks/placemark14.svg'
import placemark23 from 'assets/placemarks/placemark23.svg'
import placemark24 from 'assets/placemarks/placemark24.svg'
import placemark34 from 'assets/placemarks/placemark34.svg'
import placemark123 from 'assets/placemarks/placemark123.svg'
import placemark124 from 'assets/placemarks/placemark124.svg'
import placemark134 from 'assets/placemarks/placemark134.svg'
import placemark234 from 'assets/placemarks/placemark234.svg'
import placemark1234 from 'assets/placemarks/placemark1234.svg'
import customCluster from 'assets/placemark-cluster.svg'
import './SalonsMap.scss'
import styles from './SalonsMap.module.scss'

/** Placemark icons by the salon's divisions joined, e.g. [1, 3] → '13' */
const DIVISION_ICONS: Record<string, string> = {
  '1': placemark1,
  '2': placemark2,
  '3': placemark3,
  '4': placemark4,
  '12': placemark12,
  '13': placemark13,
  '14': placemark14,
  '23': placemark23,
  '24': placemark24,
  '34': placemark34,
  '123': placemark123,
  '124': placemark124,
  '134': placemark134,
  '234': placemark234,
  '1234': placemark1234,
}

const BALLOON_TEMPLATE = '<div class="salonContainer">' +
  '<span class="salonName">{{ properties.value.name }}</span>' +
  '<span class="salonText salonAddress">{{ properties.value.address }}</span>' +
  '<span class="salonText salonContacts">' +
  '<a  href="tel: +{{ properties.value.rawPhone }}" class="salonPhone">{{ properties.value.phone }}</a>' +
  '<span> | </span>' +
  '<span class="salonBusinessHours">Ежедневно {{ properties.value.businessHours }}</span>' +
  '</span>' +
  '<span class="salonTextLinkContainer">' +
  '<span class="salonTextLinkFix">' +
  '{% if !properties.value.link %}' +
  '' +
  '{% else %}' +
  '   <a href={{ properties.value.link }} class="salonTextLink">Подробнее</a>' +
  '{% endif %}' +
  '   <a href="#appointment" class="salonTextLink appointMentLink">Выбрать</a>' +
  '</span>' +
  '<div class="salonOptions">' +
  '  <i data-icon="kids" title="Детский уголок" class="salonOption{% if properties.value.kids %} salonOptionActive{% endif %}"></i>' +
  '  <i data-icon="coffee" title="Чай / Кофе" class="salonOption{% if properties.value.coffee %} salonOptionActive{% endif %}"></i>' +
  '  <i data-icon="ramp" title="Пандус" class="salonOption{% if properties.value.ramp %} salonOptionActive{% endif %}"></i>' +
  '  <i data-icon="parking" title="Городская парковка" class="salonOption{% if properties.value.parking %} salonOptionActive{% endif %}"></i>' +
  '  <i data-icon="wifi" title="Бесплатный WiFi" class="salonOption{% if properties.value.wifi %} salonOptionActive{% endif %}"></i>' +
  '  <i data-icon="brows" title="Услуги бровиста" class="salonOption{% if properties.value.brows %} salonOptionActive{% endif %}"></i>' +
  '</div>' +
  '</span>' +
  '</div>'

const CLUSTER_TEMPLATE = '<div style="color: #414042;  font-size: 16px; font-weight: bold;' +
  'line-height: 35px;">{{ properties.geoObjects.length }}</div>'

// The typings describe these states as control managers, but the API takes plain state objects
const TRAFFIC_STATE = { trafficShown: false, providerKey: 'traffic#archive' } as unknown as
  React.ComponentProps<typeof TrafficControl>['defaultState']
const TYPE_SELECTOR_STATE = { expanded: false } as unknown as React.ComponentProps<typeof TypeSelector>['defaultState']

/** Starts loading the map a bit before it scrolls into view */
const PRELOAD_DISTANCE = 200

type Layouts = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  cluster: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  balloon: any,
}

const createLayouts = (api: typeof ymaps, onSelectSalon: (salonId: number) => void): Layouts => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const balloon: any = api.templateLayoutFactory.createClass(BALLOON_TEMPLATE, {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    build(this: any) {
      balloon.superclass.build.call(this)
      const link = this.getParentElement().querySelector('.appointMentLink')
      link?.addEventListener('click', (e: Event) => {
        e.preventDefault()
        onSelectSalon(this.getData().properties.get('value').id)
      })
    },
  })
  return {
    cluster: api.templateLayoutFactory.createClass(CLUSTER_TEMPLATE),
    balloon,
  }
}

type SalonsMapProps = {
  /** Shows all salons around the city centre */
  city?: City | null,
  /** Shows only this salon (salon page) */
  salon?: SalonPageVars,
  onSelectSalon?: (salonId: number) => void,
  withBalloons?: boolean,
  lazy?: boolean,
}

const SalonsMapComponent: React.FC<SalonsMapProps> = (props) => {
  const { city, salon, onSelectSalon, withBalloons = true, lazy = true } = props
  const salons = useSelector(getSalons)
  const container = useRef<HTMLDivElement>(null)
  const onSelectRef = useRef(onSelectSalon)
  onSelectRef.current = onSelectSalon
  const [layouts, setLayouts] = useState<Layouts>()
  const [loading, setLoading] = useState(true)
  const [mapError, setMapError] = useState(false)
  const [ready, setReady] = useState(false)

  useEffect(() => {
    if (!lazy) {
      setReady(true)
      return undefined
    }
    const check = () => {
      if (!container.current) {
        return
      }
      const screenBottom = document.documentElement.scrollTop + document.documentElement.clientHeight + PRELOAD_DISTANCE
      if (screenBottom > container.current.offsetTop) {
        setReady(true)
      }
    }
    check()
    document.addEventListener('scroll', check, { passive: true })
    return () => document.removeEventListener('scroll', check)
  }, [lazy])

  const placemarks: Array<Pick<Salon, 'id' | 'meta'> & { divisions?: number[], businessHours?: string }> = useMemo(() => {
    if (city || !salon) {
      return salons
    }
    return [{ id: salon.salon.id, meta: { templateVars: { pageVars: salon } } as Salon['meta'] }]
  }, [city, salon, salons])

  const coords = city ? city.coords.split(',') : salon?.salonCoords.split(',')
  const mapState = {
    center: coords?.map(Number) ?? [0, 0],
    zoom: city ? 13 : 12,
    controls: ['zoomControl', 'fullscreenControl'],
  }
  const height = 435
  const width = '100%'

  const modules = [
    'control.ZoomControl',
    'control.FullscreenControl',
    'templateLayoutFactory',
    'layout.ImageWithContent',
    ...(withBalloons ? ['geoObject.addon.balloon'] : []),
  ]

  const content = ready ? (
    <>
      {loading && <Loader />}
      {mapError && <div className={styles.mapError}>Карта временно недостуна</div>}
      <Map
        state={mapState}
        modules={modules}
        height={height}
        width={width}
        onLoad={(api) => {
          setLayouts(createLayouts(api, (id) => onSelectRef.current?.(id)))
          setLoading(false)
        }}
        onError={() => {
          setLoading(false)
          setMapError(true)
        }}
      >
        <GeolocationControl options={{ float: 'left' }} />
        <TrafficControl defaultState={TRAFFIC_STATE} />
        <TypeSelector defaultState={TYPE_SELECTOR_STATE} />
        <Clusterer
          options={{
            preset: 'islands#invertedVioletClusterIcons',
            groupByCoordinates: false,
            clusterIcons: [{ href: customCluster, size: [40, 40], offset: [-20, -20] }],
            clusterIconContentLayout: layouts?.cluster,
          }}
        >
          {placemarks.map((item) => {
            const vars = item.meta?.templateVars?.pageVars
            if (!item.meta || !vars) {
              return null
            }
            const divisions = item.divisions ?? salons.find((s) => s.id === item.id)?.divisions ?? []
            const phone = vars.salonPhones?.[0]?.phone
            return <Placemark
              key={item.id}
              defaultGeometry={vars.salonCoords.split(',').map(Number)}
              options={{
                iconLayout: 'default#image',
                iconImageHref: DIVISION_ICONS[divisions.join('')],
                iconImageSize: [30, 42],
                iconImageOffset: [-5, -38],
                balloonContentLayout: layouts?.balloon,
                balloonMaxWidth: 465,
              }}
              defaultProperties={{
                value: {
                  id: item.id,
                  name: vars.salonName,
                  address: vars.salonAddress,
                  rawPhone: phone,
                  phone: formatPhone(phone ?? ''),
                  businessHours: item.businessHours,
                  wifi: vars.options.hasWifi,
                  kids: vars.options.hasKids,
                  ramp: vars.options.hasRamp,
                  coffee: vars.options.hasCoffee,
                  parking: vars.options.hasParking,
                  brows: vars.options.hasBrows,
                  link: item.meta.fullPath,
                },
              }}
            />
          })}
        </Clusterer>
      </Map>
    </>
  ) : <Loader />

  return <div ref={container} className={styles.mapContainer}>
    {content}
  </div>
}

/** Yandex map with salon placemarks; wrap it in <YMaps version={'2.1'}> */
export const SalonsMap = withYMaps(SalonsMapComponent, false, ['templateLayoutFactory', 'layout.ImageWithContent'])
